import { useCallback, type CSSProperties, type ReactElement } from "react";
import { Plus, X } from "lucide-react";
import { space } from "@tokens/index";
import { Button, IconButton, Stack, Text, TextInput, useViewport } from "@primitives/index";
import { AdminSelect } from "../admin-ui";
import type { PredicatePair } from "./ruleForm";

// One predicate row. `medium` and wider place key, value, and the remove
// control on one line. `compact` stacks key over value and keeps the
// remove control trailing the row.
const wideRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
    gap: space[2],
    alignItems: "center",
};

const compactRowStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: space[2],
    alignItems: "start",
};

interface PredicateEditorProps {
    readonly predicates: readonly PredicatePair[];
    readonly availableKeys: readonly string[];
    readonly onChange: (next: PredicatePair[]) => void;
}

export function PredicateEditor({
    predicates,
    availableKeys,
    onChange,
}: PredicateEditorProps): ReactElement {
    const { isCompact } = useViewport();

    const handleAdd = useCallback(() => {
        const used = new Set(predicates.map((p) => p.key));
        const firstUnused = availableKeys.find((k) => !used.has(k)) ?? availableKeys[0] ?? "";
        onChange([
            ...predicates,
            {
                id: crypto.randomUUID(),
                key: firstUnused,
                value: "",
            },
        ]);
    }, [predicates, availableKeys, onChange]);

    const handleUpdate = useCallback(
        (id: string, patch: Partial<Omit<PredicatePair, "id">>) => {
            onChange(predicates.map((p) => (p.id === id ? { ...p, ...patch } : p)));
        },
        [predicates, onChange],
    );

    const handleRemove = useCallback(
        (id: string) => {
            onChange(predicates.filter((p) => p.id !== id));
        },
        [predicates, onChange],
    );

    return (
        <Stack direction="column" gap={2}>
            {predicates.length === 0 && (
                <Text role="caption" tone="muted">
                    No predicates — this rule will match every work item of its kind.
                </Text>
            )}
            {predicates.map((p) => {
                const keyField = (
                    <AdminSelect
                        aria-label="Predicate key"
                        value={p.key}
                        onChange={(e) => handleUpdate(p.id, { key: e.target.value })}
                    >
                        {availableKeys.map((k) => (
                            <option key={k} value={k}>
                                {k}
                            </option>
                        ))}
                    </AdminSelect>
                );
                const valueField = (
                    <TextInput
                        fullWidth
                        aria-label="Predicate value"
                        placeholder="value (e.g. CUST-123)"
                        value={p.value}
                        onChange={(e) => handleUpdate(p.id, { value: e.target.value })}
                    />
                );
                const removeButton = (
                    <IconButton
                        icon={X}
                        aria-label="Remove predicate"
                        tooltip="Remove predicate"
                        onClick={() => handleRemove(p.id)}
                    />
                );
                return (
                    <div
                        key={p.id}
                        data-layout={isCompact ? "compact" : "wide"}
                        style={isCompact ? compactRowStyle : wideRowStyle}
                    >
                        {isCompact ? (
                            <>
                                <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
                                    {keyField}
                                    {valueField}
                                </div>
                                {removeButton}
                            </>
                        ) : (
                            <>
                                {keyField}
                                {valueField}
                                {removeButton}
                            </>
                        )}
                    </div>
                );
            })}
            <div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAdd}
                    disabled={availableKeys.length === 0}
                >
                    <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
                    Add predicate
                </Button>
            </div>
        </Stack>
    );
}
